import {PATH_SEPARATOR} from "../helper/FileHelper";
import FunctionHelper from "../helper/FunctionHelper";

/**
 * portal configuration
 */
class PortalConfiguration {
	private static _instance: PortalConfiguration;
	private _debug: boolean;
	private _production: boolean;
	private _baseUrl: URL;
	private _supportEmail: string;
	private _infoBoxDelay: number;
	private _infoBoxMaximum: number;
	private _reconnectTries: number;
	private _reconnectTimeout: number;
	private _defaultLanguage: string;
	private _fallbackLanguage: string;
	private _availableLanguages: Array<string>;
	private _fixedAdminLanguage: boolean;
	private _cachingPath: string;

	private constructor() {
		// init defaults
		this._debug = false;
		this._production = true;
		this._baseUrl = new URL("http://localhost:8080/webPDF/");
		this._supportEmail = "support@softvision.de";
		this._infoBoxDelay = 5000; // ms
		this._infoBoxMaximum = 3;
		this._reconnectTimeout = 3000; // ms
		this._reconnectTries = 5;
		this._defaultLanguage = "de";
		this._fallbackLanguage = "en";
		this._availableLanguages = ["de", "en"];
		this._fixedAdminLanguage = false;
		this._cachingPath = "";
	}

	public async init(): Promise<void> {
		let response: Response = await fetch('config.json');
		if (!response.ok) {
			return;
		}

		let configData: any = await response.json();
		this._debug = FunctionHelper.exists(configData.debug) ? configData.debug : this.debug;
		this._production = FunctionHelper.exists(configData.production) ? configData.production : this.production;
		this.baseUrl = FunctionHelper.exists(configData.baseUrl) ? new URL(configData.baseUrl) : this.baseUrl;
		this._supportEmail = FunctionHelper.exists(configData.supportEmail) ?
			configData.supportEmail : this.supportEmail;
		this._infoBoxDelay = FunctionHelper.exists(configData.infoBoxDelay) ?
			configData.infoBoxDelay : this.infoBoxDelay;
		this._infoBoxMaximum = FunctionHelper.exists(configData.infoBoxMaximum) ?
			configData.infoBoxMaximum : this.infoBoxMaximum;
		this._reconnectTimeout = FunctionHelper.exists(configData.reconnectTimeout) ?
			configData.reconnectTimeout : this.reconnectTimeout;
		this._reconnectTries = FunctionHelper.exists(configData.reconnectTries) ?
			configData.reconnectTries : this.reconnectTries;
		this._defaultLanguage = FunctionHelper.exists(configData.locale?.default) ?
			configData.locale.default : this.defaultLanguage;
		this._fallbackLanguage = FunctionHelper.exists(configData.locale?.fallback) ?
			configData.locale.fallback : this.defaultLanguage;
		this._availableLanguages = FunctionHelper.exists(configData.locale?.available) ?
			configData.locale.available : this.availableLanguages;
		this._fixedAdminLanguage = FunctionHelper.exists(configData.fixedAdminLanguage) ?
			configData.fixedAdminLanguage : this.fixedAdminLanguage;
		this._cachingPath = this._production ? ".{{WEBPDF_REV}}" : "";
	}

	/**
	 * returns the singleton configuration instance
	 */
	public static get instance(): PortalConfiguration {
		if (!FunctionHelper.exists(this._instance)) {
			this._instance = new PortalConfiguration();
		}

		return this._instance;
	}

	get debug(): boolean {
		return this._debug;
	}

	set debug(value: boolean) {
		this._debug = value;
	}

	get production(): boolean {
		return this._production;
	}

	set production(value: boolean) {
		this._production = value;
	}

	get baseUrl(): URL {
		return new URL(this._baseUrl.toString());
	}

	get portalUrl(): URL {
		if (!this.production) {
			return new URL(window.location.origin + PATH_SEPARATOR);
		}

		return this.baseUrl;
	}

	set baseUrl(value: URL) {
		if (!value.pathname.endsWith(PATH_SEPARATOR)) {
			value.pathname += PATH_SEPARATOR;
		}

		this._baseUrl = value;
	}

	get infoBoxDelay(): number {
		return this._infoBoxDelay;
	}

	set infoBoxDelay(value: number) {
		this._infoBoxDelay = value;
	}

	get infoBoxMaximum(): number {
		return this._infoBoxMaximum;
	}

	set infoBoxMaximum(value: number) {
		this._infoBoxMaximum = value;
	}

	get reconnectTries(): number {
		return this._reconnectTries;
	}

	set reconnectTries(value: number) {
		this._reconnectTries = value;
	}

	get reconnectTimeout(): number {
		return this._reconnectTimeout;
	}

	set reconnectTimeout(value: number) {
		this._reconnectTimeout = value;
	}

	get supportEmail(): string {
		return this._supportEmail;
	}

	set supportEmail(value: string) {
		this._supportEmail = value;
	}

	get defaultLanguage(): string {
		return this._defaultLanguage;
	}

	set defaultLanguage(value: string) {
		this._defaultLanguage = value;
	}

	get fallbackLanguage(): string {
		return this._fallbackLanguage;
	}

	set fallbackLanguage(value: string) {
		this._fallbackLanguage = value;
	}

	get availableLanguages(): Array<string> {
		return this._availableLanguages;
	}

	set availableLanguages(value: Array<string>) {
		this._availableLanguages = value;
	}

	get fixedAdminLanguage(): boolean {
		return this._fixedAdminLanguage;
	}

	set fixedAdminLanguage(value: boolean) {
		this._fixedAdminLanguage = value;
	}

	get cachingPath(): string {
		return this._cachingPath;
	}

	set cachingPath(value: string) {
		this._cachingPath = value;
	}
}

/**
 * the singleton configuration instance
 * @type {PortalConfiguration}
 */
const portalConfiguration: PortalConfiguration = PortalConfiguration.instance;
export default portalConfiguration;